import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  IconButton,
  FormControlLabel,
  Switch,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

function ContactTab() {
  const [contact, setContact] = useState({
    address: '',
    phone: '',
    email: '',
    mapUrl: '',
    mapEmbed: '',
    intro: '',
    contactReceiver: '',
    extra: '',
    formEnabled: true,
    social: [],
    backgroundImage: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const [newSocialPlatform, setNewSocialPlatform] = useState('');
  const [newSocialUrl, setNewSocialUrl] = useState('');

  const fetchContact = () => {
    setLoading(true);
    setError(null);
    api.get('/api/contact')
      .then(({ data }) => {
        if (!data) data = {};
        setContact(prev => ({ ...prev, ...{
          address: data.address || '',
          phone: data.phone || '',
          email: data.email || '',
          contactReceiver: data.contactReceiver || '',
          mapUrl: data.mapUrl || '',
          mapEmbed: data.mapEmbed || '',
          intro: data.intro || '',
          extra: data.extra || '',
          formEnabled: typeof data.formEnabled === 'boolean' ? data.formEnabled : true,
          social: Array.isArray(data.social) ? data.social : (data.social ? [data.social] : []),
          backgroundImage: data.backgroundImage || ''
        }}));
      })
      .catch(() => setError('Failed to load contact details'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchContact(); }, []);

  const handleChange = e => setContact(c => ({ ...c, [e.target.name]: e.target.value }));

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const res = await api.post('/api/contact', contact);
      if (!res || res.status >= 400) throw new Error('Failed to save');
      setSuccess(true);
      fetchContact();
    } catch {
      setError('Failed to save contact details');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete all contact details?')) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const res = await api.delete('/api/contact');
      if (!res || res.status >= 400) throw new Error('Failed to delete');
      setContact({ address: '', phone: '', email: '', mapUrl: '', mapEmbed: '', intro: '', extra: '', formEnabled: true, social: [], backgroundImage: '' });
      setSuccess(true);
    } catch {
      setError('Failed to delete contact details');
    } finally {
      setSaving(false);
    }
  };

  const addSocial = () => {
    if (!newSocialPlatform || !newSocialUrl) return;
    setContact(c => ({ ...c, social: [...(c.social || []), { platform: newSocialPlatform.trim().toLowerCase(), url: newSocialUrl.trim() }] }));
    setNewSocialPlatform('');
    setNewSocialUrl('');
  };

  const removeSocial = (idx) => {
    setContact(c => ({ ...c, social: c.social.filter((s, i) => i !== idx) }));
  };

  const sendTest = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = { name: 'Admin Test', email: contact.email || 'test@example.com', message: 'This is a test message from admin.' };
      const res = await api.post('/api/contact/send', payload);
      if (!res || res.status >= 400) throw new Error('Failed to send test');
      setSuccess(true);
    } catch {
      setError('Failed to send test message');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, p: 2 }}>
      <Typography variant="h5" fontWeight="bold" color="primary" sx={{ mb: 2 }}>Contact Page (CMS)</Typography>
      <Paper sx={{ p: 3, mb: 3 }}>
        <form onSubmit={handleSave}>
          <Stack spacing={2}>
            <TextField label="Intro (headline)" name="intro" value={contact.intro} onChange={handleChange} fullWidth />
            <TextField label="Address" name="address" value={contact.address} onChange={handleChange} fullWidth />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Phone" name="phone" value={contact.phone} onChange={handleChange} fullWidth />
              <TextField label="Email (public)" name="email" value={contact.email} onChange={handleChange} fullWidth />
            </Stack>
            <TextField label="Communication Email (where contact form messages go)" name="contactReceiver" value={contact.contactReceiver} onChange={handleChange} fullWidth helperText="Enter the email that should receive contact form submissions (overrides env)." />
            <TextField label="Map URL (short)" name="mapUrl" value={contact.mapUrl} onChange={handleChange} fullWidth helperText="Paste a standard Google Maps share URL or short link" />
            <TextField label="Map Embed (iframe HTML or maps link)" name="mapEmbed" value={contact.mapEmbed} onChange={handleChange} fullWidth multiline minRows={2} helperText="Paste Google Maps iframe HTML or a map link" />
            <TextField label="Background image URL" name="backgroundImage" value={contact.backgroundImage} onChange={handleChange} fullWidth helperText="Paste an image URL to display as the Contact page background" />
            {contact.backgroundImage ? (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption">Preview</Typography>
                <Box component="img" src={contact.backgroundImage} alt="background preview" sx={{ width: '100%', maxHeight: 240, objectFit: 'cover', borderRadius: 1, mt: 1 }} />
                <Button size="small" color="error" onClick={() => setContact(c => ({ ...c, backgroundImage: '' }))} sx={{ mt: 1 }}>Remove image</Button>
              </Box>
            ) : null}
            <TextField label="Extra info / Notes" name="extra" value={contact.extra} onChange={handleChange} fullWidth multiline minRows={2} helperText="Additional contact instructions or hours" />

            <FormControlLabel control={<Switch checked={!!contact.formEnabled} onChange={e => setContact(c => ({ ...c, formEnabled: e.target.checked }))} />} label="Enable public contact form" />

            <Divider />

            <Box>
              <Typography variant="subtitle2">Social links</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1, mb: 1 }}>
                <TextField placeholder="platform (eg. twitter)" size="small" value={newSocialPlatform} onChange={e => setNewSocialPlatform(e.target.value)} />
                <TextField placeholder="url" size="small" value={newSocialUrl} onChange={e => setNewSocialUrl(e.target.value)} sx={{ flex: 1 }} />
                <Button startIcon={<AddIcon />} onClick={addSocial} variant="outlined">Add</Button>
              </Stack>

              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                {(contact.social || []).map((s, i) => (
                  <Chip key={i} label={`${s.platform}: ${s.url}`} onDelete={() => removeSocial(i)} deleteIcon={<DeleteIcon />} sx={{ mb: 1 }} />
                ))}
              </Stack>
            </Box>

            <Stack direction="row" spacing={2}>
              <Button type="submit" variant="contained" color="primary" disabled={saving}>{saving ? 'Saving...' : 'Save changes'}</Button>
              <Button variant="outlined" color="secondary" onClick={fetchContact} disabled={loading}>Reload</Button>
              <Button variant="outlined" color="error" onClick={handleDelete} disabled={saving}>Delete</Button>
              <Box sx={{ flex: 1 }} />
              <Button variant="contained" color="info" onClick={sendTest} disabled={saving}>Send Test Message</Button>
            </Stack>
          </Stack>
        </form>

        {loading && <CircularProgress sx={{ mt: 2 }} />}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mt: 2 }}>Action completed successfully</Alert>}
      </Paper>
    </Box>
  );
}

export default ContactTab;
